import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast as message } from 'sonner';
import { useSidePanel, makeKey } from './SidePanelContext';
import BrowserTabs from './BrowserTabs';
import MerchantPanelBody from './merchant/MerchantPanelBody';
import { useResizableDrawer } from '../../hooks/useResizableDrawer';
import { useRecordNavigator } from '../../hooks/useRecordNavigator';
import { useGlobalMetadata } from '../../utils/providers';
import { hasOpenTransientOverlay } from '../../utils/transientOverlays';
import { workflowBreadcrumb } from '../../utils/telemetry';
import './SidePanel.css';

/**
 * Copy text, falling back to a hidden textarea. `navigator.clipboard` is
 * unavailable outside secure contexts, which includes plain-HTTP LAN testing.
 */
async function writeToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path below.
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(area);
    return copied;
  } catch {
    return false;
  }
}

function SidePanelShell() {
  const {
    openRecords,
    activeKey,
    activeRecord,
    visible,
    innerTab,
    closeTab,
    activateTab,
    reorderTabs,
    hidePanel,
    closeAllTabs,
    closeOtherTabs,
    closeTabsToRight,
  } = useSidePanel();

  const { data: metadata } = useGlobalMetadata();
  const { buildAbsoluteRecordUrl } = useRecordNavigator();

  /**
   * The panel hosts several merchants at once, so the address bar cannot name
   * "the" open record — it is never written for merchants. Sharing goes through
   * this explicit per-tab action, which copies the canonical link to the
   * clipboard, including the inner tab when copying the tab currently on screen.
   */
  const copyTabLink = useCallback((key: string) => {
    const record = openRecords.find(r => makeKey(r.table, r.id) === key);
    if (!record) return;
    const url = buildAbsoluteRecordUrl({
      tableName: record.tableName,
      id: record.id,
      table: record.table,
      tab: key === activeKey ? innerTab : undefined,
    });
    void writeToClipboard(url).then((copied) => {
      if (copied) {
        message.success('Link copied', { description: record.label });
      } else {
        // Without the clipboard the user still needs a way to get the link out.
        message.error('Could not copy the link', { description: url });
      }
    });
  }, [activeKey, buildAbsoluteRecordUrl, innerTab, openRecords]);

  const drawer = useResizableDrawer({
    storageKey: 'record_detail_drawer_width',
    defaultWidth: 520,
    viewportMargin: 24,
  });

  // Resolve display info
  const tableInfo = activeRecord ? metadata?.tableData?.byName?.[activeRecord.tableName] : null;
  const tableLabel = tableInfo?.ui_label || activeRecord?.tableName.replace(/_/g, ' ') || 'Record';

  const isMerchant = activeRecord?.tableName === 'merchant';
  const isSubmission = activeRecord?.tableName === 'submission';

  // Animate close: keep rendering during exit animation
  const shouldShow = visible && openRecords.length > 0 && isMerchant;
  const [rendered, setRendered] = useState(shouldShow);
  const [closing, setClosing] = useState(false);
  const closingTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (shouldShow) {
      setRendered(true);
      setClosing(false);
      if (closingTimer.current) clearTimeout(closingTimer.current);
    } else if (rendered) {
      setClosing(true);
      closingTimer.current = setTimeout(() => {
        setRendered(false);
        setClosing(false);
      }, 280); // match animation duration
    }
    return () => { if (closingTimer.current) clearTimeout(closingTimer.current); };
  }, [shouldShow]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shouldShow || !activeRecord) return;
    workflowBreadcrumb('record.detail.opened', {
      table: activeRecord.tableName,
      recordId: String(activeRecord.id),
      surface: 'side-panel',
    });
  }, [activeRecord, shouldShow]);

  useEffect(() => {
    if (!shouldShow) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // A dropdown, context menu or record modal on top takes the Escape;
      // dismissing the whole workspace behind it is never what was meant.
      if (hasOpenTransientOverlay()) return;
      hidePanel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [hidePanel, shouldShow]);

  if (!rendered) return null;

  return (
    <div className={`sky-panel-wrapper${closing ? ' is-closing' : ''}`}>
      <div
        className={`sky-panel${drawer.isResizing ? ' is-resizing' : ''}`}
        style={{ width: drawer.width }}
      >
        <div className="sky-panel-resize-handle" {...drawer.handleProps} />

        {/* Chrome-style browser tabs */}
        <BrowserTabs
          records={openRecords}
          activeKey={activeKey}
          onPick={activateTab}
          onClose={closeTab}
          onReorder={reorderTabs}
          onCloseAll={closeAllTabs}
          onCloseOthers={closeOtherTabs}
          onCloseToRight={closeTabsToRight}
          onCopyLink={copyTabLink}
        />

        {/* Dark header */}
        <div className="sky-panel-head">
          <div className="sky-panel-head-top">
            <div className="sky-panel-head-info">
              <div className="sky-panel-icon">
                {isSubmission ? (
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <path d="M3 10h18" /><path d="M8 4v4M16 4v4" />
                  </svg>
                ) : (
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M3 11l9-8 9 8v9a2 2 0 01-2 2h-4v-7H9v7H5a2 2 0 01-2-2z" />
                  </svg>
                )}
              </div>
              <div>
                <div className="sky-panel-eyebrow">{tableLabel}</div>
                <div className="sky-panel-record-id">#{activeRecord?.displayId ?? activeRecord?.id}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {activeKey && (
                <button
                  className="sky-panel-close"
                  onClick={() => copyTabLink(activeKey)}
                  title="Copy a shareable link to this record"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 007.5.5l3-3a5 5 0 00-7-7l-1.5 1.5" />
                    <path d="M14 11a5 5 0 00-7.5-.5l-3 3a5 5 0 007 7L12 19" />
                  </svg>
                </button>
              )}
              <button className="sky-panel-close" onClick={hidePanel} title="Hide panel">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* The panel only hosts merchant records. */}
        {activeRecord && isMerchant && (
          <MerchantPanelBody key={`${activeRecord.table}::${activeRecord.id}`} record={activeRecord} />
        )}
      </div>
    </div>
  );
}

export default React.memo(SidePanelShell);
